import React, { useRef, useState, useEffect } from 'react';
import YouTube from 'react-youtube';
import axios from 'axios';
// pausePoints object removed, now dynamic from API

const MoneyEducation = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    console.log('[MoneyEducation] Fetching videos from /videos...');
    axios.get(`${API_BASE_URL}/videos`)
      .then((response) => {
        if (!isMounted) return;
        console.log('[MoneyEducation] /videos response:', response);
        // Parse pause_points for interactive videos
        const data = Array.isArray(response.data) ? response.data : [];
        console.log('[MoneyEducation] Raw data:', data);
        const parsed = data.map(video => {
          if (video.interactive && typeof video.pause_points === 'string') {
            try {
              const parsedPausePoints = JSON.parse(video.pause_points);
              console.log(`[MoneyEducation] Parsed pause_points for video ${video.id}:`, parsedPausePoints);
              return {
                ...video,
                pause_points: parsedPausePoints
              };
            } catch (e) {
              console.warn(`[MoneyEducation] Failed to parse pause_points for video ${video.id}:`, video.pause_points, e);
              return { ...video, pause_points: [] };
            }
          }
          return { ...video, pause_points: [] };
        });
        console.log('[MoneyEducation] Parsed videos:', parsed);
        setVideos(parsed);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.error('[MoneyEducation] Error fetching videos:', err);
        setError('Failed to load videos. Please try again later.');
        setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  const categories = React.useMemo(() => {
    const cats = new Set();
    videos.forEach(video => {
      if (video.category) cats.add(video.category);
    });
    return Array.from(cats);
  }, [videos]);

  const filteredVideos = activeCategory === 'All'
    ? videos
    : videos.filter(video => video.category === activeCategory);

  useEffect(() => {
    console.log('[MoneyEducation] filteredVideos:', filteredVideos);
  }, [filteredVideos]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Money Education
        </h1>
        <p className="text-gray-600">
          Learn essential financial skills through our curated video collection
        </p>
      </div>

      {/* Category Filter */}
      <div className="mb-8 flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('All')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            activeCategory === 'All' 
              ? 'bg-blue-100 text-blue-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Topics
        </button>
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              activeCategory === category 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Video Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-blue-700 font-medium">Loading videos...</span>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center py-12">
          <span className="text-red-600 font-medium">{error}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVideos.length === 0 && (
            <div className="col-span-full text-center text-gray-500">
              <span>No videos found for this category.</span>
              {console.log('[MoneyEducation] No videos to display for current filter:', activeCategory)}
            </div>
          )}
          {filteredVideos.map((video) => (
            <div key={video.id} className="bg-white rounded-lg shadow p-4 flex flex-col">
              <div className="mb-2">
                <div className="font-semibold text-lg text-gray-900">{video.title}</div>
                <div className="text-sm text-gray-600">{video.description}</div>
              </div>
              <div className="mb-2">
                <span className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-2">{video.category}</span>
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">{video.duration}</span>
              </div>
              {/* Wrap video and category span in a single parent */}
              <div>
                {video.interactive ? (
                  <InteractiveYouTubePlayer videoId={video.video_id} pausePoints={video.pause_points || []} />
                ) : (
                  <iframe
                    className="w-full h-48"
                    src={`https://www.youtube.com/embed/${video.video_id}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                )}
                <span className="inline-block bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded">
                  {video.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Additional Resources */}
      <div className="mt-12 bg-blue-50 rounded-xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Looking for more?</h2>
        <p className="text-gray-700 mb-4">
          We're constantly adding new educational content. Check back regularly for new videos.
        </p>
      </div>
    </div>
  );
};

// Interactive YouTube player component
function InteractiveYouTubePlayer({ videoId, pausePoints }) {
  const playerRef = useRef(null);
  const [pausedAt, setPausedAt] = useState(null);
  const [currentPauseIdx, setCurrentPauseIdx] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);

  // Pause video at pausePoints
  const handleStateChange = (event) => {
    if (event.data === window.YT?.PlayerState.PLAYING && pausePoints?.length > 0) {
      const interval = setInterval(() => {
        const currentTime = event.target.getCurrentTime();
        if (
          currentPauseIdx < pausePoints.length &&
          currentTime >= pausePoints[currentPauseIdx] &&
          (!pausedAt || Math.abs(currentTime - pausePoints[currentPauseIdx]) < 1)
        ) {
          event.target.pauseVideo();
          setPausedAt(pausePoints[currentPauseIdx]);
          setCurrentPauseIdx((idx) => idx + 1);
          clearInterval(interval);
        }
      }, 250);
      // Clear interval when paused or stopped
      event.target.addEventListener('onStateChange', (e) => {
        if (e.data !== window.YT?.PlayerState.PLAYING) clearInterval(interval);
      });
    }
  };

  // Store player instance
  const handleReady = (event) => {
    playerRef.current = event.target;
    setPlayerReady(true);
  };

  // Continue playback
  const handleContinue = () => {
    if (playerRef.current) {
      playerRef.current.playVideo();
      setPausedAt(null);
    }
  };

  // Reset pause state if video is replayed
  const handlePlayback = (event) => {
    if (event.data === window.YT?.PlayerState.PLAYING && pausedAt !== null) {
      setPausedAt(null);
    }
    if (event.data === window.YT?.PlayerState.ENDED) {
      setCurrentPauseIdx(0);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <YouTube
        videoId={videoId}
        className="w-full h-48"
        opts={{ width: '100%', height: '238' }}
        onReady={handleReady}
        onStateChange={(e) => {
          handleStateChange(e);
          handlePlayback(e);
        }}
      />
      {pausedAt !== null && (
        <button
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none"
          onClick={handleContinue}
        >
          Continue
        </button>
      )}
    </div>
  );
}

export default MoneyEducation;
